import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TranslatableEntity } from "@evennt-tech/api-v2-core";
import type { IAiTranslationBodyReq } from "@evennt-tech/api-v2-external";
import { apiV2 } from "api/firebase";
import { encodeBase64Payload } from "tools/common/base64";
import { notify } from "tools/snackbar";
import {
  AI_TRANSLATION_ERROR,
  isBlankTranslation,
  toTranslationValues,
} from "tools/translation";
import type { ShowLanguage } from "view/components/SettingsRows/ShowLanguagesRow/useShowLanguages";
import {
  LocalisationSaveOptions,
  LocalisationTarget,
  TranslationValues,
} from "ts/app/translation";
import { useTranslationLanguages } from "./useTranslationLanguages";

type ValuesByTarget = Record<string, TranslationValues>;

export type LocalisationStorage = {
  load: () => ValuesByTarget;
  save: (values: ValuesByTarget) => Promise<void> | void;
  signature?: string;
};

export type UseLocalisationParams = {
  showId?: string;
  /**
   * Library content: AI translation is entitled through the company's global
   * language set instead of a Show. Pass the company languages as `languages`.
   */
  organiserCompanyId?: string;
  languages?: ShowLanguage[];
  storage?: LocalisationStorage;
  targets: LocalisationTarget[];
  enabled?: boolean;
};

export type LocalisationController = {
  languages: ShowLanguage[];
  languageCodes: string[];
  hasLanguages: boolean;
  isLoading: boolean;
  hasLoaded: boolean;
  loadFailed: boolean;
  isDirty: boolean;
  /** Some field translated somewhere, but not every field in every language. */
  isPartiallyTranslated: boolean;
  targets: LocalisationTarget[];
  registerTargets: (id: string, targets: LocalisationTarget[]) => () => void;
  getTarget: (key: string) => LocalisationTarget | undefined;
  getValue: (key: string, languageCode: string) => string;
  setValue: (key: string, languageCode: string, text: string) => void;
  /**
   * Replaces a target's values as if they had just been loaded — nothing
   * becomes dirty. For translations owned elsewhere that the host knows
   * changed (a master saved from `Edit from source` over a read-only panel).
   */
  seed: (key: string, values: TranslationValues) => void;
  getRevision: (key: string, languageCode: string) => number;
  hasAnyContent: (key: string) => boolean;
  hasContent: (key: string, languageCode: string) => boolean;
  isComplete: (key: string) => boolean;
  isBusy: (key: string, languageCode?: string) => boolean;
  /** Any auto-translation still in flight, whatever the target. */
  isAnyBusy: boolean;
  canTranslate: (key: string) => boolean;
  translate: (key: string, languageCode?: string) => Promise<void>;
  clear: (key: string) => void;
  save: (options?: LocalisationSaveOptions) => Promise<void>;
  /**
   * Re-reads every persisted target's translations from the server, merging
   * them in without clobbering unsaved edits (only missing language codes are
   * filled). For values a SEPARATE controller wrote that this one never
   * observed — e.g. the Tags modal saving a brand-new tag's translations, which
   * this host has already fetched (empty) before that save landed.
   */
  refetch: () => void;
};

const busyKey = (key: string, languageCode?: string) =>
  languageCode ? `${key}::${languageCode}` : key;

const targetIdentity = (target: LocalisationTarget) =>
  `${target.entity}|${target.entityId ?? ""}|${target.field}`;

/**
 * Holds the per-language text for one modal/form.
 *
 * State lives OUTSIDE react-hook-form on purpose: the API validates with
 * `forbidNonWhitelisted`, so a stray `translations` key on a form whose values
 * are spread onto the wire would 400 the entity save.
 */
export const useLocalisation = ({
  showId,
  organiserCompanyId,
  languages: languagesOverride,
  storage,
  targets,
  enabled = true,
}: UseLocalisationParams): LocalisationController => {
  const {
    languages: showLanguages,
    languageCodes: showLanguageCodes,
    hasLanguages: showHasLanguages,
    isLoading: isLanguagesLoading,
  } = useTranslationLanguages(showId);

  const languages = languagesOverride ?? showLanguages;
  const languageCodes = languagesOverride
    ? languagesOverride.map(({ code }) => code)
    : showLanguageCodes;
  const hasLanguages = languagesOverride
    ? languagesOverride.length > 0
    : showHasLanguages;

  const [values, setValues] = useState<ValuesByTarget>({});
  const [initial, setInitial] = useState<ValuesByTarget>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [revisions, setRevisions] = useState<Record<string, number>>({});
  const [isFetching, setIsFetching] = useState(false);
  // `targetIdentity(...)` of every target fetched at least once; feeds `hasLoaded`.
  const [fetchedIdentities, setFetchedIdentities] = useState<
    Record<string, true>
  >({});
  const [storageLoaded, setStorageLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  // Bumped by `refetch()` to force the load effect to re-run against unchanged
  // targets — a fetch key that never depends on the fetched content.
  const [reloadNonce, setReloadNonce] = useState(0);

  const [registered, setRegistered] = useState<
    Record<string, LocalisationTarget[]>
  >({});

  const registerTargets = useCallback(
    (id: string, list: LocalisationTarget[]) => {
      setRegistered((prev) => ({ ...prev, [id]: list }));

      return () =>
        setRegistered((prev) => {
          const { [id]: _removed, ...rest } = prev;

          return rest;
        });
    },
    []
  );

  const allTargets = useMemo(() => {
    const merged = [...Object.values(registered).flat(), ...targets];
    const seen = new Set<string>();

    return merged.filter((target) => {
      if (seen.has(target.key)) return false;

      seen.add(target.key);

      return true;
    });
  }, [registered, targets]);

  /**
   * Loaded = every target that points at a persisted entity has been fetched.
   * Entity ids arrive in waves (the task drawer resolves separator/subtask/
   * option ids from the deep graph after the first fetch), so a "first fetch
   * finished" flag would report loaded while a later wave is still in flight.
   */
  const hasLoaded = storage
    ? storageLoaded
    : allTargets.every(
        (target) =>
          !target.entityId || !!fetchedIdentities[targetIdentity(target)]
      );

  const targetsRef = useRef(allTargets);
  targetsRef.current = allTargets;

  const identityByKeyRef = useRef<Record<string, string>>({});

  const fetchSignature = useMemo(
    () =>
      allTargets
        .filter((target) => !!target.entityId)
        .map((target) => targetIdentity(target))
        .sort()
        .join(","),
    [allTargets]
  );

  const identitySignature = useMemo(
    () =>
      allTargets
        .map((target) => `${target.key}=${targetIdentity(target)}`)
        .sort()
        .join(","),
    [allTargets]
  );

  /**
   * Drop values whose key was re-pointed at a different entity. "Different"
   * means TWO KNOWN IDS THAT DIFFER, and nothing else — a key routinely gains
   * or loses its id without moving anywhere, and treating that as a re-point
   * wipes what the organiser just typed.
   */
  useEffect(() => {
    const stale = targetsRef.current
      .filter((target) => {
        const previous = identityByKeyRef.current[target.key];

        if (previous === undefined) return false;

        const [, previousEntityId] = previous.split("|");
        const nextEntityId = target.entityId ?? "";

        return (
          !!previousEntityId &&
          !!nextEntityId &&
          previous !== targetIdentity(target)
        );
      })
      .map((target) => target.key);

    targetsRef.current.forEach((target) => {
      identityByKeyRef.current[target.key] = targetIdentity(target);
    });

    if (!stale.length) return;

    const drop = (prev: ValuesByTarget) => {
      const next = { ...prev };
      stale.forEach((key) => delete next[key]);
      return next;
    };

    setValues(drop);
    setInitial(drop);
    setRevisions((prev) => {
      const next = { ...prev };
      stale.forEach((key) => {
        next[key] = (next[key] ?? 0) + 1;
      });
      return next;
    });
  }, [identitySignature]);

  useEffect(() => {
    if (enabled) return;

    identityByKeyRef.current = {};
    setValues({});
    setInitial({});
    setBusy({});
    setFetchedIdentities({});
    setStorageLoaded(false);
    setLoadFailed(false);
    setRevisions((prev) =>
      Object.keys(prev).reduce<Record<string, number>>((acc, key) => {
        acc[key] = prev[key] + 1;
        return acc;
      }, {})
    );
  }, [enabled]);

  const shouldFetch = enabled && hasLanguages && !storage && !!fetchSignature;

  const loadRef = useRef(storage?.load);
  loadRef.current = storage?.load;

  const storageSignature = storage?.signature;

  useEffect(() => {
    if (!enabled || !hasLanguages || !loadRef.current) return;

    const seeded = loadRef.current();

    setValues(seeded);
    setInitial(seeded);
    setStorageLoaded(true);
  }, [enabled, hasLanguages, storageSignature]);

  useEffect(() => {
    if (!enabled || !hasLanguages || storage) return undefined;

    if (!fetchSignature) return undefined;

    let cancelled = false;

    const load = async () => {
      const persisted = targetsRef.current.filter(
        (target) => !!target.entityId
      );

      const byEntity = persisted.reduce<
        Record<string, { entityIds: string[]; fields: string[] }>
      >((acc, target) => {
        const bucket = acc[target.entity] ?? { entityIds: [], fields: [] };
        if (!bucket.entityIds.includes(target.entityId as string)) {
          bucket.entityIds.push(target.entityId as string);
        }
        if (!bucket.fields.includes(target.field)) {
          bucket.fields.push(target.field);
        }
        acc[target.entity] = bucket;
        return acc;
      }, {});

      setIsFetching(true);
      try {
        let failed = false;
        const responses = await Promise.all(
          Object.keys(byEntity).map((entity) =>
            apiV2.translation
              .getMany({
                entity: entity as TranslatableEntity,
                entityIds: byEntity[entity].entityIds,
                fields: byEntity[entity].fields,
              })
              .catch((e) => {
                console.error(`Failed to load ${entity} translations: `, e);
                failed = true;
                return null;
              })
          )
        );

        if (cancelled) return;

        setLoadFailed(failed);

        const byEntityIdAndField: Record<string, TranslationValues> = {};
        responses.forEach((response) => {
          if (!response) return;

          response.items.forEach((item) => {
            byEntityIdAndField[
              `${response.entity}|${item.entityId}|${item.field}`
            ] = toTranslationValues(item.translations);
          });
        });

        const loaded = persisted.reduce<ValuesByTarget>((acc, target) => {
          const stored = byEntityIdAndField[targetIdentity(target)];
          if (stored) acc[target.key] = stored;
          return acc;
        }, {});

        const filled: string[] = [];
        setValues((prev) => {
          const next = { ...prev };

          Object.keys(loaded).forEach((key) => {
            const current = prev[key] ?? {};
            const merged = { ...current };
            let changed = false;

            Object.keys(loaded[key]).forEach((code) => {
              if (code in current) return;
              merged[code] = loaded[key][code];
              changed = true;
              filled.push(busyKey(key, code));
            });

            if (changed) next[key] = merged;
          });

          return next;
        });

        setInitial((prev) => {
          const next = { ...prev };
          Object.keys(loaded).forEach((key) => {
            next[key] = { ...(prev[key] ?? {}), ...loaded[key] };
          });
          return next;
        });

        setRevisions((prev) => {
          const next = { ...prev };
          filled.forEach((flag) => {
            next[flag] = (next[flag] ?? 0) + 1;
          });
          return next;
        });

        // LAST on purpose — React 17 doesn't batch in promise callbacks, and
        // `hasLoaded` may only flip after the merged values above have landed.
        // Failed batches still count as fetched (the load-failed alert covers
        // them), otherwise `hasLoaded` would hang false forever.
        setFetchedIdentities((prev) => {
          const next = { ...prev };
          persisted.forEach((target) => {
            next[targetIdentity(target)] = true;
          });
          return next;
        });
      } finally {
        if (!cancelled) {
          setIsFetching(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, hasLanguages, shouldFetch, fetchSignature, reloadNonce]);

  const getTarget = useCallback(
    (key: string) => targetsRef.current.find((target) => target.key === key),
    []
  );

  const getValue = useCallback(
    (key: string, languageCode: string) => values[key]?.[languageCode] ?? "",
    [values]
  );

  const setValue = useCallback(
    (key: string, languageCode: string, text: string) => {
      setValues((prev) => ({
        ...prev,
        [key]: { ...(prev[key] ?? {}), [languageCode]: text },
      }));
    },
    []
  );

  const seed = useCallback((key: string, next: TranslationValues) => {
    setValues((prev) => ({ ...prev, [key]: { ...next } }));
    setInitial((prev) => ({ ...prev, [key]: { ...next } }));
    setRevisions((prev) => ({ ...prev, [key]: (prev[key] ?? 0) + 1 }));
  }, []);

  const getRevision = useCallback(
    (key: string, languageCode: string) =>
      (revisions[key] ?? 0) + (revisions[busyKey(key, languageCode)] ?? 0),
    [revisions]
  );

  const hasContent = useCallback(
    (key: string, languageCode: string) =>
      !isBlankTranslation(values[key]?.[languageCode]),
    [values]
  );

  const hasAnyContent = useCallback(
    (key: string) => languageCodes.some((code) => hasContent(key, code)),
    [languageCodes, hasContent]
  );

  const isComplete = useCallback(
    (key: string) =>
      languageCodes.length > 0 &&
      languageCodes.every((code) => hasContent(key, code)),
    [languageCodes, hasContent]
  );

  /**
   * Something is translated, but not every field in every language — the
   * sections' shared default expanded state (see LocalisationSection).
   */
  const isPartiallyTranslated = useMemo(
    () =>
      allTargets.some((target) => hasAnyContent(target.key)) &&
      !allTargets.every((target) => isComplete(target.key)),
    [allTargets, hasAnyContent, isComplete]
  );

  const isBusy = useCallback(
    (key: string, languageCode?: string) => {
      if (languageCode) {
        return !!busy[busyKey(key, languageCode)] || !!busy[key];
      }

      return Object.keys(busy).some(
        (flag) => flag === key || flag.indexOf(`${key}::`) === 0
      );
    },
    [busy]
  );

  const isAnyBusy = useMemo(() => Object.values(busy).some(Boolean), [busy]);

  const canTranslate = useCallback(
    (key: string) => {
      if (!showId && !languageCodes.length) return false;
      const target = getTarget(key);
      if (!target) return false;
      return !isBlankTranslation(target.getSourceText());
    },
    [showId, languageCodes, getTarget]
  );

  const translate = useCallback(
    async (key: string, languageCode?: string) => {
      const target = getTarget(key);
      if (!target) return;
      if (!showId && !languageCodes.length) return;

      const sourceText = target.getSourceText();
      if (isBlankTranslation(sourceText)) return;
      const isRichText = target.kind === "richText";
      const text = isRichText
        ? encodeBase64Payload(sourceText) ?? sourceText
        : sourceText;

      const flag = busyKey(key, languageCode);
      setBusy((prev) => ({ ...prev, [flag]: true }));

      try {
        const requested = languageCode ? { languageCodes: [languageCode] } : {};
        let body: IAiTranslationBodyReq;

        if (showId) {
          body = { text, showId, ...requested };
        } else if (organiserCompanyId) {
          body = { text, organiserCompanyId, ...requested };
        } else {
          body = {
            text,
            languageCodes: languageCode ? [languageCode] : languageCodes,
          };
        }

        const generated = await (isRichText
          ? apiV2.aiTranslation.translateHtml(body)
          : apiV2.aiTranslation.translate(body));

        const applicable = Object.keys(generated).filter(
          (code) =>
            languageCodes.indexOf(code) !== -1 &&
            !isBlankTranslation(generated[code])
        );
        if (!applicable.length) return;

        setValues((prev) => {
          const current = prev[key] ?? {};
          const next = { ...current };
          applicable.forEach((code) => {
            next[code] = generated[code];
          });
          return { ...prev, [key]: next };
        });
        setRevisions((prev) => {
          const next = { ...prev };
          applicable.forEach((code) => {
            const flag = busyKey(key, code);
            next[flag] = (next[flag] ?? 0) + 1;
          });
          return next;
        });
      } catch (e) {
        console.error("Failed to generate translations: ", e);
        notify(AI_TRANSLATION_ERROR, { variant: "error" });
      } finally {
        setBusy((prev) => {
          const next = { ...prev };
          delete next[flag];
          return next;
        });
      }
    },
    [getTarget, showId, organiserCompanyId, languageCodes]
  );

  const clear = useCallback(
    (key: string) => {
      setValues((prev) => ({
        ...prev,
        [key]: languageCodes.reduce<TranslationValues>((acc, code) => {
          acc[code] = "";
          return acc;
        }, {}),
      }));
      setRevisions((prev) => ({ ...prev, [key]: (prev[key] ?? 0) + 1 }));
    },
    [languageCodes]
  );

  const getChangedCodes = useCallback(
    (key: string) =>
      languageCodes.filter((code) => {
        const before = initial[key]?.[code];
        const after = values[key]?.[code];

        if (after === undefined) return false;
        if (isBlankTranslation(before) && isBlankTranslation(after)) {
          return false;
        }

        return (before ?? "") !== (after ?? "");
      }),
    [languageCodes, initial, values]
  );

  const isDirty = useMemo(
    () => allTargets.some((target) => getChangedCodes(target.key).length > 0),
    [allTargets, getChangedCodes]
  );

  const refetch = useCallback(() => {
    setReloadNonce((nonce) => nonce + 1);
  }, []);

  const save = useCallback(
    async ({
      resolveEntityId,
      skipLanguageCodes = [],
    }: LocalisationSaveOptions = {}) => {
      if (!hasLanguages) return;

      if (storage) {
        await storage.save(values);
        setInitial(values);
        return;
      }

      const pending = targetsRef.current
        .map((target) => ({
          target,
          codes: getChangedCodes(target.key).filter(
            (code) => !skipLanguageCodes.includes(code)
          ),
        }))
        .filter(({ codes }) => codes.length > 0);

      if (!pending.length) return;

      const saved: ValuesByTarget = {};

      // Sequential on purpose: concurrent upserts on the same entity race on the
      // lazily-created `translation_to_fields` row.
      for (const { target, codes } of pending) {
        const entityId = resolveEntityId?.(target) ?? target.entityId;
        if (!entityId) {
          // eslint-disable-next-line no-continue
          continue;
        }

        const translations = codes.map((languageCode) => {
          const text = values[target.key]?.[languageCode];

          return {
            languageCode,
            text: isBlankTranslation(text) ? "" : (text as string),
          };
        });

        // eslint-disable-next-line no-await-in-loop
        await apiV2.translation.upsert({
          entity: target.entity,
          entityId,
          field: target.field,
          translations,
        });

        saved[target.key] = {
          ...(initial[target.key] ?? {}),
          ...toTranslationValues(translations),
        };
      }

      setInitial((prev) => ({ ...prev, ...saved }));
    },
    [hasLanguages, getChangedCodes, values, initial, storage]
  );

  return {
    languages,
    languageCodes,
    hasLanguages,
    isLoading: isLanguagesLoading || isFetching,
    hasLoaded: hasLanguages ? hasLoaded : true,
    loadFailed,
    isDirty,
    isPartiallyTranslated,
    targets: allTargets,
    registerTargets,
    getTarget,
    getValue,
    setValue,
    seed,
    getRevision,
    hasAnyContent,
    hasContent,
    isComplete,
    isBusy,
    isAnyBusy,
    canTranslate,
    translate,
    clear,
    save,
    refetch,
  };
};

export default useLocalisation;
